import { useState, useMemo } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { HelpCircle, Search, X, BookOpen } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { getVisibleSectionsForRole } from "@/data/trainingContent";

/**
 * Floating help button for staff headers — opens a slide-in drawer
 * with quick-search into the full training manual. Clicking a result
 * calls onOpenFullManual(sectionId) so the host page can switch to
 * the full manual tab (with that section expanded).
 */
export default function TrainingHelpButton({ onOpenFullManual, variant = "header" }) {
  const { user } = useAuth();
  const role = user?.role || "prescriber";
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const sections = useMemo(() => getVisibleSectionsForRole(role), [role]);

  const matches = useMemo(() => {
    if (!q.trim()) return sections.slice(0, 8);
    const needle = q.toLowerCase();
    return sections
      .filter((s) => s.title.toLowerCase().includes(needle) || s.body.toLowerCase().includes(needle))
      .slice(0, 20);
  }, [q, sections]);

  const btnClass = variant === "header"
    ? "p-2 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800 transition-colors"
    : "p-2 rounded-lg text-slate-500 hover:text-slate-900 hover:bg-slate-100 transition-colors";

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className={btnClass}
        title="Training Manual — Quick Help"
        data-testid="training-help-button"
      >
        <HelpCircle className="w-5 h-5" />
      </button>

      {open && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black/50 z-40"
            onClick={() => setOpen(false)}
            data-testid="training-help-backdrop"
          />
          {/* Drawer */}
          <aside
            className="fixed right-0 top-0 bottom-0 w-full max-w-md bg-white dark:bg-slate-900 z-50 shadow-2xl flex flex-col"
            data-testid="training-help-drawer"
          >
            <div className="flex items-center justify-between p-4 border-b dark:border-slate-800">
              <div className="flex items-center gap-2">
                <BookOpen className="w-4 h-4 text-purple-600 dark:text-purple-400" />
                <h3 className="font-semibold text-slate-900 dark:text-white">Quick Help</h3>
              </div>
              <button
                onClick={() => setOpen(false)}
                className="p-1 rounded hover:bg-slate-100 dark:hover:bg-slate-800"
                data-testid="training-help-close"
              >
                <X className="w-4 h-4 text-slate-500" />
              </button>
            </div>

            <div className="p-4 border-b dark:border-slate-800">
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  autoFocus
                  placeholder="Search training manual..."
                  className="pl-9"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  data-testid="training-help-search"
                />
              </div>
            </div>

            <div className="flex-1 overflow-auto p-2">
              {matches.length === 0 ? (
                <p className="text-sm text-slate-500 dark:text-slate-400 p-4 text-center">
                  No matches for &quot;{q}&quot;
                </p>
              ) : (
                <div className="space-y-1">
                  {matches.map((s) => {
                    const snippet = s.body.replace(/\*\*/g, "").slice(0, 140);
                    return (
                      <button
                        key={s.id}
                        onClick={() => {
                          setOpen(false);
                          if (onOpenFullManual) onOpenFullManual(s.id);
                        }}
                        className="w-full text-left p-3 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                        data-testid={`training-help-result-${s.id}`}
                      >
                        <div className="font-medium text-sm text-slate-900 dark:text-white">{s.title}</div>
                        <div className="text-xs text-slate-500 dark:text-slate-400 line-clamp-2 mt-0.5">
                          {snippet}{s.body.length > 140 ? "…" : ""}
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="p-3 border-t dark:border-slate-800">
              <Button
                className="w-full"
                variant="outline"
                onClick={() => {
                  setOpen(false);
                  if (onOpenFullManual) onOpenFullManual(null);
                }}
                data-testid="training-help-open-full"
              >
                Open Full Training Manual
              </Button>
            </div>
          </aside>
        </>
      )}
    </>
  );
}
